import React, { useCallback, useState, useEffect } from 'react';
import NewProgramContent, {
  NewProgramContentHandler,
} from './NewProgramContent';
import SuccessMessage from '../NewReport/SuccessMessage';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TrueImpactModal from 'shared/design-system/components/TrueImpactModal';
import OrgNameWarningModal, {
  matchesOrgName,
} from '../LevelOneFeedback/ProgramNameWarningModal';
import { useClient } from 'shared/apollo';
import { Program } from '../../Reports/NewReportDialog';
import { RequestState } from 'shared/auth/hooks/useRequestState';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { SuccessResponse } from '../NewReport/api';
import { NPOUser, createProgram } from './api';
import { useUserActions } from 'shared/auth/hooks/useUserActions';
import { useFeatureFlags } from 'shared/providers/FeatureFlags/useFeatureFlags';
import { ContributionEligibility } from '../ContributionEligibility/ContributionEligibilitySelector';

const SUCCESS_HANDOFF_DELAY_MS = 0;

interface NewProgramProps {
  open: boolean;
  allNPOUsers: RequestState<{
    allUsers: NPOUser[];
  }>;
  programsList: RequestState<{
    allPrograms: Program[];
  }>;
  reloadProgramList: (programId?: number) => unknown;
  onClose: (programId?: number) => unknown;
}

const isProgramExist = (
  allPrograms: Program[],
  programName: string,
): boolean => {
  const index = allPrograms.findIndex(p => p.name === programName);
  return index !== -1;
};

const defaultErrorText = 'Something went wrong.';
const NewProgram: React.FC<NewProgramProps> = ({
  open,
  allNPOUsers,
  programsList,
  onClose,
  reloadProgramList,
}) => {
  const { organizationName, isThirdParty } = useUserActions();
  const client = useClient();
  const { newProgramVisibility } = useUserActions();
  const levelOneFeedbackEnabled = useFeatureFlags('levelOneFeedback');
  const ref = React.useRef<NewProgramContentHandler>(null);
  const [errorText, setErrorText] = useState<string | undefined>(undefined);
  const [showOrgNameWarning, setShowOrgNameWarning] = useState(false);
  const [pendingProgramData, setPendingProgramData] = useState<{
    managerId: number;
    programName: string;
    contributionEligibility: ContributionEligibility | undefined;
  } | null>(null);
  const [newProgram, makeNewProgramRequest] = useGraphQLRequest<
    SuccessResponse<'newProgram'>
  >();
  const programProfilingEnabled = useFeatureFlags('programProfiling');
  const [handingOff, setHandingOff] = useState<boolean>(false);

  useEffect(() => {
    if (newProgram.type === 'REQUEST_SUCCESS') {
      const createdId = newProgram.data.newProgram.id;
      reloadProgramList(createdId);
      if (!programProfilingEnabled || createdId == null) return;
      // Show the success screen briefly, then hand off to the parent to
      // open the Program Questions wizard. The parent owns both the URL
      // and the questions modal so we don't end up with two
      // ProgramQuestionsModal instances (one scoped here, one scoped
      // there) fighting for close.
      setHandingOff(true);
      const timer = window.setTimeout(() => {
        setHandingOff(false);
        onClose(createdId);
      }, SUCCESS_HANDOFF_DELAY_MS);
      return () => window.clearTimeout(timer);
    }
    if (newProgram.type === 'REQUEST_ERROR') {
      setErrorText(newProgram.error.message || defaultErrorText);
      return;
    }
  }, [newProgram.type, reloadProgramList, programProfilingEnabled]);

  const onSubmit = useCallback(async (donorNameResolved = false) => {
    if (!ref.current || programsList.type !== 'REQUEST_SUCCESS') {
      setErrorText(defaultErrorText);
      return;
    }
    const { answer, errorText } = ref.current.answer();
    if (errorText || !answer) {
      setErrorText(errorText || defaultErrorText);
      return;
    }
    if (isProgramExist(programsList.data.allPrograms, answer.programName)) {
      setErrorText('Program name already exists.');
      return;
    }
    setErrorText(undefined);
    // The backend re-validates and forces PRIVATE; this only asks the user first.
    if (!donorNameResolved && (await ref.current.checkDonorName())) {
      return;
    }
    if (
      levelOneFeedbackEnabled &&
      matchesOrgName(answer.programName, organizationName)
    ) {
      setPendingProgramData({
        managerId: answer.manager.userId,
        programName: answer.programName,
        contributionEligibility: answer.contributionEligibility ?? undefined,
      });
      setShowOrgNameWarning(true);
      return;
    }
    makeNewProgramRequest(
      createProgram(client!, {
        logoFileId: null,
        managerId: answer.manager.userId,
        name: answer.programName,
        status: 'active',
        visibility: isThirdParty ? 'PUBLIC' : newProgramVisibility,
        contributionEligibility: answer.contributionEligibility ?? undefined,
      }),
    );
  }, [
    client,
    programsList,
    makeNewProgramRequest,
    newProgramVisibility,
    organizationName,
    levelOneFeedbackEnabled,
    isThirdParty,
  ]);

  const handleConfirmedSubmit = useCallback(
    (level1Note: string) => {
      if (!pendingProgramData) return;
      setShowOrgNameWarning(false);
      makeNewProgramRequest(
        createProgram(client!, {
          logoFileId: null,
          managerId: pendingProgramData.managerId,
          name: pendingProgramData.programName,
          status: 'active',
          visibility: isThirdParty ? 'PUBLIC' : newProgramVisibility,
          level1Note,
          contributionEligibility: pendingProgramData.contributionEligibility,
        }),
      );
    },
    [
      client,
      pendingProgramData,
      makeNewProgramRequest,
      newProgramVisibility,
      isThirdParty,
    ],
  );

  const success = newProgram.type === 'REQUEST_SUCCESS';
  const handleClose = () => {
    if (handingOff) return;
    onClose(newProgram.data?.newProgram?.id);
  };

  return (
    <>
      <OrgNameWarningModal
        open={showOrgNameWarning}
        isMatchedDonorName={false}
        onClose={() => setShowOrgNameWarning(false)}
        onConfirm={handleConfirmedSubmit}
        programName={pendingProgramData?.programName}
        confirmText="Create"
      />
      <TrueImpactModal
        title="Add a new program"
        open={open}
        onClose={handleClose}
        maxWidth="1000px"
        next={
          success
            ? undefined
            : {
                text: 'Create Program',
                disabled: handingOff,
                isLoading: newProgram.type === 'REQUEST_START',
                onClick: () => onSubmit(),
              }
        }
        previous={{
          text: newProgram.type === 'REQUEST_SUCCESS' ? 'Close' : 'Cancel',
          disabled: handingOff,
          isLoading: false,
          onClick: handleClose,
        }}
        errorText={errorText}
        showExpandButton={true}
        initialExpandValue={isThirdParty}
      >
        {programsList.type !== 'REQUEST_SUCCESS' ? (
          <TrueImpactLoading />
        ) : success ? (
          <SuccessMessage heading="Your program was successfully created!" />
        ) : (
          <NewProgramContent
            ref={ref}
            allNPOUsers={allNPOUsers}
            programsList={programsList}
            onDonorNameKept={() => onSubmit(true)}
          />
        )}
      </TrueImpactModal>
    </>
  );
};

export default NewProgram;
